import React, { useEffect, useState } from "react";
import styled from "styled-components";
import dayjs, { Dayjs } from "dayjs";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { LanguageNameSpaces } from "@shared/types";
import Modal from "common/Modal/Modal.tsx";
import Select from "common/Select/Select.tsx";
import Input from "common/Inputs/Input.tsx";
import CustomInputNumber from "common/Inputs/CustomInputNumber.tsx";
import DatePicker from "common/DatePicker/DatePicker.tsx";
import SegmentTabs from "common/NewLook/SegmentTabs.tsx";
import RequiredFieldMessage from "common/Animations/RequiredFieldMessage.tsx";
import { displayResultMessage } from "utils/misc.tsx";
import { useCreateFinanceEntryMutation } from "app/services/finance.service";
import {
  FinanceCategory,
  FinanceCurrency,
  FinanceDirection,
} from "features/finance/types";
import {
  DIRECTION_TO_LABEL,
  FINANCE_CURRENCIES,
  FINANCE_DATE_FORMAT,
} from "features/finance/constants";
import useCategoryOptions from "features/finance/useCategoryOptions";
import {
  Field,
  FieldHint,
  FieldInline,
  FieldLabel,
  Fields,
} from "features/finance/formStyles";

interface AddEntryModalProps {
  open: boolean;
  onClose: () => void;
  defaultCurrency: FinanceCurrency;
}

type DirectionKey = "income" | "expense";

const AddEntryModal = ({
  open,
  onClose,
  defaultCurrency,
}: AddEntryModalProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ADMIN);
  const [direction, setDirection] = useState(FinanceDirection.EXPENSE);
  const [category, setCategory] = useState<FinanceCategory>();
  const [date, setDate] = useState<Dayjs | null>(dayjs());
  const [amount, setAmount] = useState<string | null>();
  const [currency, setCurrency] = useState(defaultCurrency);
  const [description, setDescription] = useState("");
  const [showErrors, setShowErrors] = useState(false);
  const categoryOptions = useCategoryOptions(direction);
  const [createEntry, { isLoading }] = useCreateFinanceEntryMutation();

  const parsedAmount = parseFloat(amount ?? "");
  const isAmountValid = Number.isFinite(parsedAmount) && parsedAmount > 0;
  const trimmedDescription = description.trim();

  useEffect(() => {
    if (open) setCurrency(defaultCurrency);
  }, [open, defaultCurrency]);

  const reset = () => {
    setDirection(FinanceDirection.EXPENSE);
    setCategory(undefined);
    setDate(dayjs());
    setAmount(undefined);
    setDescription("");
    setShowErrors(false);
  };

  const close = () => {
    reset();
    onClose();
  };

  const onDirectionChange = (key: DirectionKey) => {
    setDirection(DIRECTION_KEY_TO_VALUE[key]);
    setCategory(undefined);
  };

  const onSubmit = () => {
    if (!category || !date || !isAmountValid || !trimmedDescription) {
      setShowErrors(true);
      return;
    }
    displayResultMessage(
      createEntry({
        direction,
        category,
        date: date.format(FINANCE_DATE_FORMAT),
        amount: parsedAmount,
        currency,
        description: trimmedDescription,
      }).unwrap(),
      t("finance.add_entry.success"),
      undefined,
      undefined,
      undefined,
      undefined,
      true,
      true
    )
      .then(close)
      .catch(() => {});
  };

  return (
    <Modal
      open={open}
      title={t("finance.add_entry.title")}
      onOk={onSubmit}
      okText={t("finance.add_entry.submit")}
      okButtonProps={{ loading: isLoading }}
      onCancel={close}
    >
      <Fields>
        <FieldHint>{t("finance.add_entry.hint")}</FieldHint>
        <Field>
          <FieldLabel>{t("finance.add_entry.direction")}</FieldLabel>
          <FieldInline>
            <SegmentTabs<DirectionKey>
              options={DIRECTION_KEYS}
              value={
                direction === FinanceDirection.INCOME ? "income" : "expense"
              }
              onChange={onDirectionChange}
              getLabel={(key) =>
                t(DIRECTION_TO_LABEL[DIRECTION_KEY_TO_VALUE[key]])
              }
              ariaLabel={t("finance.add_entry.direction")}
            />
          </FieldInline>
        </Field>
        <Field>
          <FieldLabel>{t("finance.add_entry.category")}</FieldLabel>
          <Select
            value={category}
            options={categoryOptions}
            onChange={setCategory}
            placeholder={t("finance.add_entry.category")}
            mobileTitle={t("finance.add_entry.category")}
            style={{ width: "100%" }}
          />
          <RequiredFieldMessage
            requirement={showErrors && !category}
            errorMessage={t("finance.add_entry.errors.category")}
          />
        </Field>
        <Row>
          <Field>
            <FieldLabel>{t("finance.add_entry.date")}</FieldLabel>
            <DatePicker
              value={date}
              onChange={(value) => setDate(value as Dayjs | null)}
              allowClear={false}
              disabledDate={(current) => current.isAfter(dayjs(), "day")}
              style={{ width: "100%" }}
            />
            <RequiredFieldMessage
              requirement={showErrors && !date}
              errorMessage={t("finance.add_entry.errors.date")}
            />
          </Field>
          <Field>
            <FieldLabel>{t("finance.add_entry.currency")}</FieldLabel>
            <FieldInline>
              <SegmentTabs
                options={FINANCE_CURRENCIES}
                value={currency}
                onChange={setCurrency}
                ariaLabel={t("finance.add_entry.currency")}
              />
            </FieldInline>
          </Field>
        </Row>
        <Field>
          <FieldLabel>{t("finance.add_entry.amount")}</FieldLabel>
          <CustomInputNumber
            value={amount}
            onChange={setAmount}
            stringMode={true}
            min={0}
            precision={2}
            placeholder="0.00"
          />
          <RequiredFieldMessage
            requirement={showErrors && !isAmountValid}
            errorMessage={t("finance.add_entry.errors.amount")}
          />
        </Field>
        <Field>
          <FieldLabel>{t("finance.add_entry.description")}</FieldLabel>
          <Input
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder={t("finance.add_entry.description_placeholder")}
            maxLength={255}
          />
          <RequiredFieldMessage
            requirement={showErrors && !trimmedDescription}
            errorMessage={t("finance.add_entry.errors.description")}
          />
        </Field>
      </Fields>
    </Modal>
  );
};

export default AddEntryModal;

const DIRECTION_KEYS: readonly DirectionKey[] = ["expense", "income"];
const DIRECTION_KEY_TO_VALUE: Record<DirectionKey, FinanceDirection> = {
  income: FinanceDirection.INCOME,
  expense: FinanceDirection.EXPENSE,
};

const Row = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 16px;
`;
